import React, { FC, useRef, useState } from 'react';

import { Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Payment } from '@mui/icons-material';
import BraintreeDropinContainer from './braintree-dropin-container';
import { useBraintreeContext } from '@acorn-portal/shared/src/context/braintree-context';
import {
	CustomPaymentMethodPayload,
	ExtendPaymentMethodOptions,
} from '@acorn-portal/shared/src/utils/types/extensions/braintree';
import { PaymentClient } from '@acorn-portal/shared/src/services/api';
import API from '@acorn-portal/shared/src/services/AxiosInstance';

interface IBrainTreeError {
	message: string;
	name: string;
	[key: string]: unknown;
}

interface IProps {
	amount?: string;
	submitPayment: (payload: CustomPaymentMethodPayload) => Promise<void>;
	clientToken: string;
	loading: boolean;
	isOptOut: boolean;
	setErrorMessage?: (value?: string) => void;
	onSubmitting?: (value: boolean) => void;
	cardOnly?: boolean;
}

const SimpleBraintreePaymentForm: FC<IProps> = ({
	loading,
	amount,
	submitPayment,
	clientToken,
	isOptOut,
	setErrorMessage,
	onSubmitting,
	cardOnly,
}) => {
	const [internalLoading, setInternalLoading] = useState(false);
	const submitButton = useRef<HTMLButtonElement>(null);
	const { instance, isPaymentDetailsValid } = useBraintreeContext();

	const onSubmitPayment = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		event.preventDefault();
		if (!instance) return;
		setInternalLoading(true);
		if (onSubmitting) onSubmitting(true);
		try {
			if (!amount) {
				if (setErrorMessage) setErrorMessage('No amount for payment provided');
				return;
			}
			const { clientIp } = await new PaymentClient(undefined, API).getClientData();
			const requestPayload: ExtendPaymentMethodOptions = {
				threeDSecure: {
					amount,
					challengeRequested: true,
					additionalInformation: { ipAddress: clientIp },
				},
			};
			const payload = await instance.requestPaymentMethod(requestPayload);
			await submitPayment(payload as CustomPaymentMethodPayload);
		} catch (e: unknown) {
			const err = e as IBrainTreeError;
			instance.clearSelectedPaymentMethod();
			if (err.message && err.name === 'DropinError') {
				if (setErrorMessage) setErrorMessage(err.message);
			}
		} finally {
			setInternalLoading(false);
			if (onSubmitting) onSubmitting(false);
		}
	};

	return (
		<Box>
			<BraintreeDropinContainer clientToken={clientToken} amount={amount} isOptOut={isOptOut} cardOnly={cardOnly} />
			{instance && (
				<LoadingButton
					variant='contained'
					color='primary'
					ref={submitButton}
					onClick={onSubmitPayment}
					startIcon={<Payment />}
					size='large'
					fullWidth
					sx={(theme) => ({ marginTop: theme.spacing(1) })}
					loading={internalLoading || loading}
					disabled={!isPaymentDetailsValid}
				>
					Process Payment
				</LoadingButton>
			)}
		</Box>
	);
};

export default SimpleBraintreePaymentForm;
